import { AuditHistoryPanel, type AuditRecordView } from '@/components/autoforms/AuditHistoryPanel';
import { AuditStatusBar } from '@/components/autoforms/AuditStatusBar';
import { StatusTransitionsBar } from '@/components/autoforms/StatusTransitionsBar';
import type { ReactNode } from 'react';
import { TasksLinkedTab } from '@/components/autoforms/TasksLinkedTab';
import { FileListTab } from '@/components/autoforms/FileListTab';
import { SubordinateTab } from '@/components/autoforms/SubordinateTab';
import { LockConflictView } from '@/components/autoforms/LockConflictView';
import { CustomFormContent, type CustomFieldAttr, type CustomTsAttr } from '@/components/autoforms/CustomFormContent';
import { AutoFormContent, type AutoFieldDef, type AutoTsDef } from '@/components/autoforms/AutoFormContent';
import { ObjectFormDialogs, type PickerAttrRef, type AnyRefPickerState, type MultiPickState, type ModulePickerState } from '@/components/autoforms/ObjectFormDialogs';
import { CopyButton } from '@/components/ui/CopyButton';
import { formatValue } from '@/lib/format';
import type { LockConflict } from '@/hooks/useObjectLock';

// ============================================================================
// ObjectFormBody — тело формы объекта (вынесено из AutoObjectForm, E2e11):
// saveError-баннер, статус-панель, диспетчер вкладок (Основное/подчинённые/
// Задачи/Файлы), история изменений, loading/error/lock-состояния, содержимое
// (кастомная/автоформа) и диалоговые оверлеи. ЧИСТО отображательный компонент
// (SRP): ВСЯ запись/состояние и логика — в AutoObjectForm (через колбэки).
// ============================================================================

export interface SubordinateRef {
  name: string;
  synonym: string;
}

interface CustomFormViewProps {
  dlCreate: { type: string; name: string; initial: Record<string, unknown> } | null;
  dlOpen: { type: string; name: string; id: string } | null;
  assignOpen: boolean;
  assignBusy: boolean;
  assignTitle: string;
  onDlCreateClose: () => void;
  onDlOpenClose: () => void;
  onAssignClose: () => void;
  onAssignSelect: (id: string) => void;
  onPick: (attr: any, tsName?: string, rowIndex?: number) => void;
  onPickTs: (tsName: string, rowIndex: number, colAttr: { name: string; types?: Array<{ typeName: string; fullName: string; objectType: string }> }) => void;
  onCreateElement: (table: string, filters: Array<{ field: string; condition: string; value?: string }>, objectId: string) => void;
  onOpenElement: (table: string, id: string) => void;
  onPickMulti: (tsName: string, colMeta: any) => void;
  onCommand: (command: string) => void;
}

export interface ObjectFormBodyProps extends CustomFormViewProps {
  // Метаданные
  fields: AutoFieldDef[];
  objectName: string;
  objectType: string;
  objectId: string | undefined;
  table: string;
  // Данные
  data: Record<string, any>;
  onDataChange: (updater: (prev: Record<string, any>) => Record<string, any>) => void;
  customForm: any;
  customFormLayout: any;
  customFormObjectAttrs: CustomFieldAttr[];
  customFormTabularSections: CustomTsAttr[];
  // Вкладки
  tabs: Array<{ id: string; label: string }>;
  activeTab: string;
  onTabChange: (tab: string) => void;
  subordinates: SubordinateRef[];
  // Статус
  statusSchema: { fieldName: string; schemaName: string } | null;
  statusPanel: { currentCode: string; currentName: string; transitions: any[] } | null;
  statusBusy: boolean;
  onApplyStatus: (code: string) => void;
  // Аудит
  audit: { lastRecord?: AuditRecordView | null; records: AuditRecordView[]; loading: boolean; error?: string | null };
  // СЭД: панель «Согласование» (маршруты) — рендерится после статус-панели
  approval?: ReactNode;
  // Теги «что угодно» (чипы + пикер) — существующие записи
  tags?: ReactNode;
  auditEnabled: boolean;
  canViewAudit: boolean;
  historyOpen: boolean;
  onHistoryToggle: () => void;
  // Состояния
  loading: boolean;
  error: string | null;
  saveError: string | null;
  lockConflict: LockConflict | null;
  isAdmin: boolean;
  onRequestRelease: (lockId: string) => Promise<void>;
  onForceRelease: (lockId: string) => Promise<void>;
  formReadOnly: boolean;
  formEnabled: boolean;
  validationError: string | null;
  // Реквизиты формы/действия
  formKey: string;
  displayNames: Record<string, string>;
  onDisplayNamesChange: (fn: (prev: Record<string, string>) => Record<string, string>) => void;
  tsData: Record<string, Array<Record<string, unknown>>>;
  onTsDataChange: (updater: (prev: Record<string, Array<Record<string, unknown>>>) => Record<string, Array<Record<string, unknown>>>) => void;
  hiddenFields: Record<string, boolean>;
  formAttrs: Array<{ name: string; source?: any }>;
  tabularSections: AutoTsDef[];
  tsParentId: string;
  isFileCatalog: boolean;
  fileBusy: boolean;
  fileError: string | null;
  fileLocked: boolean;
  fileDownloadable: boolean;
  fileDisplayName: string;
  fileSizeText: string;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFileDownload: () => void;
  renderField: (f: AutoFieldDef) => React.ReactNode;
  fieldErrors: Record<string, string>;
  catalogHierarchyType: string;
  catalogOwners: Array<{ code: string; type: string }>;
  // Диалоги
  pickerOpen: boolean;
  pickingAttr: PickerAttrRef | null;
  anyRefPicker: AnyRefPickerState | null;
  multiPick: MultiPickState | null;
  modulePicker: ModulePickerState | null;
  onPickerClose: () => void;
  onPickerSelect: (id: string, name: string) => void;
  onAnyRefClose: () => void;
  onAnyRefTypeSelect: (t: { objectType: string; objectName: string }) => void;
  onAnyRefValueSelect: (id: string, name: string) => void;
  onAnyRefFile: (file: File) => void;
  onMultiClose: () => void;
  onMultiSelect: (id: string, name: string) => void;
  onModuleClose: () => void;
  onModuleSelect: (id: string, name: string) => void;
}

export function ObjectFormBody(props: ObjectFormBodyProps) {
  const {
    fields, objectName, objectType, objectId, table, data, onDataChange, customForm, customFormLayout,
    customFormObjectAttrs, customFormTabularSections, tabs, activeTab, onTabChange, subordinates,
    statusSchema, statusPanel, statusBusy, onApplyStatus, audit, approval, tags, auditEnabled, canViewAudit,
    historyOpen, onHistoryToggle, loading, error, saveError, lockConflict, isAdmin, onRequestRelease,
    onForceRelease, formReadOnly, formEnabled, validationError, formKey, displayNames,
    onDisplayNamesChange, tsData, onTsDataChange, hiddenFields, formAttrs, tabularSections, tsParentId,
    isFileCatalog, fileBusy, fileError, fileLocked, fileDownloadable, fileDisplayName, fileSizeText,
    onFileUpload, onFileDownload, renderField, fieldErrors, catalogHierarchyType, catalogOwners,
    pickerOpen, pickingAttr, anyRefPicker, multiPick, modulePicker, onPickerClose, onPickerSelect,
    onAnyRefClose, onAnyRefTypeSelect, onAnyRefValueSelect, onAnyRefFile, onMultiClose, onMultiSelect,
    onModuleClose, onModuleSelect, dlCreate, dlOpen, assignOpen, assignBusy, assignTitle, onDlCreateClose,
    onDlOpenClose, onAssignClose, onAssignSelect, onPick, onPickTs, onCreateElement, onOpenElement, onPickMulti, onCommand,
  } = props;

  const mainTab = activeTab === 'main';
  const hasAudit = !!(data.created_at || data.created_by || data.updated_at || data.updated_by);

  return (
    <div className="space-y-3">
      {/* Ошибка СОХРАНЕНИЯ — баннер ПОВЕРХ формы (поля остаются видимыми) */}
      {saveError && (
        <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
          <span className="flex-1 min-w-0 break-words">{saveError}</span>
          <CopyButton text={saveError} compact />
        </div>
      )}
      {/* Статусная модель / согласование / теги — ТОЛЬКО на основной вкладке
          (на «Задачи»/«Файлы»/подчинённых вкладках они не выводятся). */}
      {mainTab && statusSchema && !loading && !error && !lockConflict && (
        <StatusTransitionsBar
          currentName={statusPanel?.currentName}
          currentCode={statusPanel?.currentCode}
          transitions={statusPanel?.transitions}
          busy={statusBusy}
          disabled={formReadOnly || !formEnabled || !objectId}
          hasRecord={!!objectId}
          onApply={onApplyStatus}
        />
      )}
      {/* СЭД: согласование записи — существующие записи (маршруты на сервере) */}
      {mainTab && objectId && approval && !loading && !error && !lockConflict && approval}
      {mainTab && objectId && tags && !loading && !error && !lockConflict && tags}
      {activeTab === 'tasks' ? (
        <TasksLinkedTab
          subjectType={objectType}
          subjectTable={table}
          subjectId={objectId || ''}
          subjectPresentation={String(data.name ?? data.code ?? data.number ?? objectName)}
        />
      ) : activeTab === 'files' ? (
        <FileListTab
          objectName="files"
          ownerId={objectId || ''}
        />
      ) : activeTab.startsWith('sub:') ? (
        (() => {
          const sub = subordinates.find((s) => `sub:${s.name}` === activeTab);
          return sub && !loading && !error && !lockConflict ? (
            <SubordinateTab objectName={sub.name} synonym={sub.synonym} ownerId={objectId!} />
          ) : null;
        })()
      ) : (
        <>
        {!loading && !error && !lockConflict && objectId && auditEnabled && canViewAudit && (
          <AuditHistoryPanel
            lastRecord={audit.lastRecord}
            records={audit.records}
            loading={audit.loading}
            error={audit.error}
            open={historyOpen}
            onToggle={onHistoryToggle}
          />
        )}
        {/* Компактная полоса стандартных команд задачи/заявки ПЕРЕНЕСЕНА в шапку
            окна (на место «Сохранить/Отмена», см. ObjectFormWindowHeader —
            commandBar). Здесь НЕ рендерится, чтобы не дублировать кнопки. */}
        {loading ? (
          <div className="flex h-20 items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-md px-4 py-2">
            <span className="flex-1 min-w-0 break-words">{error}</span>
            <CopyButton text={error} compact />
          </div>
        ) : lockConflict ? (
          <LockConflictView
            conflict={lockConflict}
            isAdmin={isAdmin}
            onRequestRelease={onRequestRelease}
            onForceRelease={onForceRelease}
          />
        ) : customForm ? (
          // Кастомная форма (макет + реквизиты формы; логика/состояние — в родителе)
          <CustomFormContent
            dlCreate={dlCreate}
            dlOpen={dlOpen}
            assignOpen={assignOpen}
            assignBusy={assignBusy}
            assignTitle={assignTitle}
            validationError={validationError}
            formKey={formKey}
            elements={customFormLayout?.elements || []}
            displayNames={displayNames}
            onDisplayNamesChange={onDisplayNamesChange}
            tsData={tsData}
            onTsDataChange={onTsDataChange}
            objectAttrs={customFormObjectAttrs}
            tabularSections={customFormTabularSections}
            formReadOnly={formReadOnly}
            formEnabled={formEnabled}
            hiddenFields={hiddenFields}
            formAttrs={formAttrs}
            recordId={objectId || undefined}
            onDlCreateClose={onDlCreateClose}
            onDlOpenClose={onDlOpenClose}
            onAssignClose={onAssignClose}
            onAssignSelect={onAssignSelect}
            onPick={onPick}
            onPickTs={onPickTs}
            onCreateElement={onCreateElement}
            onOpenElement={onOpenElement}
            onPickMulti={onPickMulti}
            onCommand={onCommand}
          />
        ) : (
          // Автоформа
          <AutoFormContent
            validationError={validationError}
            fields={fields}
            data={data}
            onDataChange={onDataChange}
            fieldErrors={fieldErrors}
            renderField={renderField}
            hierarchyType={catalogHierarchyType}
            owners={catalogOwners}
            objectName={objectName}
            tabularSections={tabularSections}
            tsParentId={tsParentId}
            readOnly={formReadOnly}
            isFileCatalog={isFileCatalog}
            fileBusy={fileBusy}
            fileError={fileError}
            fileLocked={fileLocked}
            fileDownloadable={fileDownloadable}
            fileDisplayName={fileDisplayName}
            fileSizeText={fileSizeText}
            onFileUpload={onFileUpload}
            onFileDownload={onFileDownload}
          />
        )}
        {/* Системные аудит-поля — ОДНОЙ строкой В САМОМ НИЗУ формы (Создал /
            Дата создания / Изменил / Дата изменения), только текст без полей
            ввода; в макете кастомной формы аудит-поля не рендерятся (нет дублей,
            нет рамок). Применяется ко всем типам: справочники/документы/задачи/
            заявки/регистры. */}
        {objectId && hasAudit && <AuditStatusBar data={data} />}
        <ObjectFormDialogs
          pickingAttr={pickerOpen && pickingAttr ? pickingAttr : null}
          anyRefPicker={anyRefPicker}
          multiPick={multiPick}
          modulePicker={modulePicker}
          onPickerClose={onPickerClose}
          onPickerSelect={onPickerSelect}
          onAnyRefClose={onAnyRefClose}
          onAnyRefTypeSelect={onAnyRefTypeSelect}
          onAnyRefValueSelect={onAnyRefValueSelect}
          onAnyRefFile={onAnyRefFile}
          onMultiClose={onMultiClose}
          onMultiSelect={onMultiSelect}
          onModuleClose={onModuleClose}
          onModuleSelect={onModuleSelect}
        />
        </>
      )}
    </div>
  );
}
